import { useCallback, useMemo } from 'react';

import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { HistoryServiceFactory } from '@/modules/canvas/services/history/HistoryServiceFactory';
import { logger } from '@/modules/common/logging/logger';
import { Template } from '@/modules/floorplanDesign/types';
import { TpsServiceFactory } from '@/modules/templating';

import { DimensionItem, toItemUpdates } from './items';

/**
 * @description Applies moved items and templates like dragging them on the canvas: one undo step,
 * templates are regenerated, and every one of them stays selected (they can get a new id)
 */
export const useMoveWithTemplates = (selectedIds: string[]) => {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const tpsSvc = useMemo(() => TpsServiceFactory.getInstance().createTpsService(), []);
  const historySvc = useMemo(() => HistoryServiceFactory.getInstance().createHistoryService(), []);

  return useCallback(
    async (items: DimensionItem[], templates: Template[]) => {
      try {
        const regeneratedIds = await historySvc.batchAsync(async () => {
          if (items.length > 0) fddmSvc.updateItems(toItemUpdates(items));
          return tpsSvc.regenerateTemplates(templates);
        });
        // Regenerated templates can get a new id: keep every one of them selected
        tpsSvc.selectTemplates(selectedIds.map((id) => regeneratedIds.get(id) ?? id));
      } catch (error: unknown) {
        logger.error('Failed to move the selection with templates.', error);
      }
    },
    [fddmSvc, historySvc, selectedIds, tpsSvc],
  );
};
